import { useEffect, useRef, useState } from 'react'
import { Check, Link2, Send, Share2, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'

/**
 * Tombol bagikan.
 *
 * Di peranti yang menyediakannya, lembar bagi bawaan sistem dipakai lebih
 * dulu: daftar aplikasinya sudah sesuai dengan yang benar-benar terpasang
 * pada peranti itu, dan pilihan kita sendiri tidak akan pernah selengkap itu.
 * Peramban meja yang tidak menyediakannya mendapat menu tautan biasa.
 *
 * Tidak ada satu pun skrip pihak ketiga di sini. Tombol bagikan bawaan
 * jejaring sosial memuat kode dari domain mereka pada setiap kunjungan
 * halaman, dan itu berarti setiap pembaca halaman ini ikut terlacak
 * meskipun tidak pernah menekan apa pun.
 */
export function ShareButton({
  title,
  text,
  url,
  className,
  variant = 'outline',
  size = 'sm',
  label = 'Bagikan',
}: {
  title: string
  text?: string
  /** Bila kosong, alamat halaman yang sedang dibuka yang dipakai. */
  url?: string
  className?: string
  variant?: 'outline' | 'ghost' | 'secondary'
  size?: 'sm' | 'md'
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const shareUrl = url ?? (typeof window === 'undefined' ? '' : window.location.href)
  const shareText = text ?? title

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const share = async () => {
    // navigator.share hanya tersedia pada konteks aman dan menolak panggilan
    // yang tidak berasal dari tindakan pengguna; keduanya terpenuhi di sini.
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: shareUrl })
        return
      } catch (error) {
        // Pengguna yang menutup lembar bagi memunculkan AbortError. Itu
        // bukan kegagalan, jadi menu cadangan pun tidak perlu dibuka.
        if (error instanceof Error && error.name === 'AbortError') return
      }
    }
    setOpen((current) => !current)
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  const encodedUrl = encodeURIComponent(shareUrl)
  const encodedText = encodeURIComponent(shareText)

  const targets = [
    { label: 'WhatsApp', href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`, icon: <WhatsAppIcon /> },
    { label: 'Telegram', href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, icon: <Send className="size-4" /> },
    { label: 'X', href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`, icon: <XIcon /> },
    { label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, icon: <FacebookIcon /> },
    { label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, icon: <LinkedInIcon /> },
  ]

  return (
    // inline-block, bukan block: menu berlabuh pada tepi kanan wadahnya, dan
    // wadah selebar halaman akan melemparkan menu jauh dari tombolnya.
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <Button variant={variant} size={size} onClick={share} aria-expanded={open} aria-haspopup="menu">
        <Share2 className="size-4" />
        {label}
      </Button>

      {open ? (
        <div
          role="menu"
          className="slide-in absolute right-0 z-30 mt-2 w-60 overflow-hidden rounded-2xl border border-line bg-raised shadow-[var(--shadow-lifted)]"
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <span className="mono-label text-[11px] text-subtle">Bagikan lewat</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Tutup menu bagikan"
              className="rounded-full p-1 text-subtle transition hover:text-ink"
            >
              <X className="size-3.5" />
            </button>
          </div>

          <ul className="p-1.5">
            {targets.map((target) => (
              <li key={target.label}>
                <a
                  role="menuitem"
                  href={target.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-ink transition hover:bg-sunken"
                >
                  <span className="text-muted">{target.icon}</span>
                  {target.label}
                </a>
              </li>
            ))}

            <li>
              <button
                type="button"
                role="menuitem"
                onClick={copy}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-ink transition hover:bg-sunken"
              >
                <span className={copied ? 'text-brand' : 'text-muted'}>
                  {copied ? <Check className="size-4" /> : <Link2 className="size-4" />}
                </span>
                {copied ? 'Tautan tersalin' : 'Salin tautan'}
              </button>
            </li>
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/* Lambang jejaring sosial digambar sendiri, bukan dimuat dari domain mereka:
   memuatnya dari sana membuat setiap pembaca halaman ikut terlacak. */

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden>
      <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47 0 1.46 1.06 2.87 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35z"/>
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.87 9.87 0 0 0 4.75 1.21h.01c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm0 18.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.24-8.23a8.23 8.23 0 0 1 0 16.47z"/>
    </svg>
  )
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden>
      <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.65l-5.22-6.82-5.96 6.82H1.68l7.73-8.84L1.25 2.25h6.82l4.71 6.23 5.46-6.23zm-1.16 17.52h1.83L7.01 4.13H5.05l12.03 15.64z"/>
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden>
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.44 2.91h-2.34v7.03C18.34 21.24 22 17.08 22 12.06z"/>
    </svg>
  )
}

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden>
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/>
    </svg>
  )
}
